import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { PnLReportData } from './types';
import {
  filterTreeByGastosView,
  formatNumberPdf,
  formatVariationPdf,
  getLatestBudget,
  getLatestVariation,
  getNumeric,
  getReportColumns,
} from './utils';

const COLORS = {
  primary: [15, 23, 42] as [number, number, number],
  success: [5, 150, 105] as [number, number, number],
  successLight: [236, 253, 245] as [number, number, number],
  danger: [220, 38, 38] as [number, number, number],
  muted: [100, 116, 139] as [number, number, number],
  sectionBg: [241, 245, 249] as [number, number, number],
};

function createDoc() {
  return new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'letter',
  });
}

function addHeader(doc: jsPDF, data: PnLReportData) {
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 28, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Estado de Resultados', 14, 13);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `${data.companyName} · ${data.periodLabel} · Moneda: CRC`,
    14,
    21
  );
}

function findKpi(data: PnLReportData, cuenta: string): { current: number | null; previous: number | null; variation: number | null } {
  let row;
  data.tree.forEach(section => {
    section.children?.forEach(child => {
      if (child.row?.Cuenta === cuenta) {
        row = child.row;
      }
    });
  });

  const reportColumns = getReportColumns(data.periodColumns, data.selectedMonths);
  const realColumns = reportColumns.filter(c => !c.isBudget && c.date);
  const latest = realColumns[realColumns.length - 1];
  const previous = realColumns[realColumns.length - 2];

  const current = latest && row ? getNumeric(row, latest.key) : null;
  const previousValue = previous && row ? getNumeric(row, previous.key) : null;
  const variation =
    current !== null && previousValue !== null
      ? ((current - previousValue) / Math.abs(previousValue)) * 100
      : null;

  return { current, previous: previousValue, variation };
}

function addKpiCard(
  doc: jsPDF,
  label: string,
  value: string,
  change: string,
  x: number,
  y: number,
  color: [number, number, number]
) {
  doc.setFillColor(...color);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(x, y, 60, 22, 3, 3, 'FD');

  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(label, x + 4, y + 7);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(value, x + 4, y + 14);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(change, x + 4, y + 19);
}

export function exportPnLToPdf(data: PnLReportData, filename?: string) {
  const doc = createDoc();
  addHeader(doc, data);

  const tree = filterTreeByGastosView(data.tree, data.gastosView);
  const reportColumns = getReportColumns(data.periodColumns, data.selectedMonths);
  const realColumns = reportColumns.filter(c => !c.isBudget && c.date);
  const hasBudget = reportColumns.some(c => c.isBudget);

  // KPIs
  const ventasNetas = findKpi(data, 'VENTAS_NETAS');
  const utilidadBruta = findKpi(data, 'UTILIDAD_BRUTA');
  const margenContribucion = findKpi(data, 'MARGEN CONTRIBUCIÓN');
  const utilidadNeta = findKpi(data, 'UTILIDAD NETA');

  const kpis = [
    {
      label: 'Ventas Netas',
      value: formatNumberPdf(ventasNetas.current),
      change: formatVariationPdf(ventasNetas.variation),
      color: [219, 234, 254] as [number, number, number],
    },
    {
      label: 'Utilidad Bruta',
      value: formatNumberPdf(utilidadBruta.current),
      change: formatVariationPdf(utilidadBruta.variation),
      color: [220, 252, 231] as [number, number, number],
    },
    {
      label: 'Margen Contribución',
      value: formatNumberPdf(margenContribucion.current),
      change: formatVariationPdf(margenContribucion.variation),
      color: [254, 243, 199] as [number, number, number],
    },
    {
      label: 'Utilidad Neta',
      value: formatNumberPdf(utilidadNeta.current),
      change: formatVariationPdf(utilidadNeta.variation),
      color: [254, 226, 226] as [number, number, number],
    },
  ];

  const startY = 36;
  const kpiSpacing = 66;
  kpis.forEach((kpi, i) => {
    addKpiCard(doc, kpi.label, kpi.value, kpi.change, 14 + i * kpiSpacing, startY, kpi.color);
  });

  // Tabla
  const head = ['CONCEPTO', ...realColumns.map(c => c.yearMonth)];
  if (hasBudget) head.push('PRESUPUESTO');
  head.push('% VS MES ANT.');

  const body: (string | number)[][] = [];

  tree.forEach(section => {
    body.push([`${section.label}  (${section.children?.length ?? 0} conceptos)`, ...Array(head.length - 1).fill('')]);

    section.children?.forEach(child => {
      const label = `${'    '.repeat(Math.min(child.level + 1, 2))}${child.label}`;
      const variation = getLatestVariation(child.row, reportColumns);
      const budget = hasBudget ? getLatestBudget(child.row, reportColumns) : null;

      const row: (string | number)[] = [label];
      realColumns.forEach(col => {
        const value = getNumeric(child.row, col.key);
        row.push(value !== null ? formatNumberPdf(value) : '—');
      });

      if (hasBudget) {
        row.push(budget !== null ? formatNumberPdf(budget) : '—');
      }

      row.push(formatVariationPdf(variation));
      body.push(row);
    });

    body.push(Array(head.length).fill(''));
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const usableWidth = pageWidth - margin * 2;
  const conceptWidth = 60;
  const remainingWidth = usableWidth - conceptWidth;
  const valueColWidth = remainingWidth / (head.length - 1);

  autoTable(doc, {
    startY: startY + 30,
    head: [head],
    body,
    headStyles: {
      fillColor: COLORS.primary,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 7,
      cellPadding: 1.5,
    },
    bodyStyles: {
      fontSize: 6.5,
      textColor: COLORS.primary,
      cellPadding: 1,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: {
      0: { cellWidth: conceptWidth },
      ...Object.fromEntries(
        Array.from({ length: head.length - 1 }, (_, i) => [i + 1, { cellWidth: valueColWidth, halign: 'right' }])
      ),
    },
    styles: {
      lineColor: [226, 232, 240],
      lineWidth: 0.2,
      font: 'helvetica',
      overflow: 'linebreak',
    },
    didParseCell: hookData => {
      if (hookData.section === 'body') {
        const rowIdx = hookData.row.index;
        const row = body[rowIdx];
        const firstCell = String(row?.[0] ?? '');
        const isSection = firstCell.includes('conceptos') && row?.slice(1).every(v => v === '');
        const isTotal = firstCell.trim() !== '' && !firstCell.startsWith('      ') && !isSection;

        if (isSection) {
          hookData.cell.styles.fillColor = COLORS.sectionBg;
          hookData.cell.styles.fontStyle = 'bold';
        } else if (isTotal) {
          hookData.cell.styles.fillColor = COLORS.successLight;
          hookData.cell.styles.fontStyle = 'bold';
        }

        if (hookData.column.index === head.length - 1) {
          const text = String(hookData.cell.raw);
          if (text.includes('+')) {
            hookData.cell.styles.textColor = COLORS.danger;
          } else if (text.includes('-')) {
            hookData.cell.styles.textColor = COLORS.success;
          }
        }
      }
    },
    didDrawPage: pageData => {
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.muted);
      doc.text(
        `Página ${String(pageData.pageNumber)}`,
        doc.internal.pageSize.getWidth() - 14,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'right' }
      );
    },
  });

  doc.save(filename ?? `estado-resultados-${data.companyName}-${data.periodLabel}.pdf`);
}
